// frontend/src/pages/reports/MarkRangeReportPage.js
import React, { useState, useEffect } from 'react';
import {
  Table, Card, Space, Button, InputNumber, Select, Spin, Typography, Row, Col,
  Statistic, Divider, Empty, Tag, message
} from 'antd';
import { DownloadOutlined, PrinterOutlined } from '@ant-design/icons';
import { getAllClasses } from '../../services/classService';
import { getAllExams } from '../../services/examService';
import { getClassReport } from '../../services/markService';
import { useAcademicYear } from '../../context/AcademicYearContext';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const MarkRangeReportPage = () => {
  const { currentYear } = useAcademicYear();
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [minPercentage, setMinPercentage] = useState(0);
  const [maxPercentage, setMaxPercentage] = useState(100);
  const [reportData, setReportData] = useState(null);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(15);
  const [stats, setStats] = useState({ average: 0, total: 0, count: 0 });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [classesData, examsData] = await Promise.all([
          getAllClasses(),
          getAllExams()
        ]);
        setClasses(classesData);
        setExams(examsData);
      } catch (error) {
        message.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentYear]);

  useEffect(() => {
    if (!selectedClass || !selectedExam) {
      setReportData(null);
      setFilteredStudents([]);
      return;
    }

    const fetchReport = async () => {
      setLoading(true);
      try {
        const data = await getClassReport(selectedClass, selectedExam);
        const subjects = data.subjects || [];

        // Compute canonical total marks for each subject (max seen in the data)
        const subjectTotals = subjects.reduce((acc, s) => ({ ...acc, [s.id]: 0 }), {});
        data.students.forEach(student => {
          (student.subjects || []).forEach(sub => {
            const tm = sub.total_marks ? parseFloat(sub.total_marks) : 0;
            if (tm > subjectTotals[sub.subject_id]) {
              subjectTotals[sub.subject_id] = tm;
            }
          });
        });

        // Calculate class total marks (sum of all subject totals)
        const classTotalPossible = Object.values(subjectTotals).reduce((sum, t) => sum + t, 0);

        // Process data to calculate totals and percentages for each student
        const processedStudents = data.students.map(student => {
          let totalScore = 0;
          let marksCount = 0;

          student.subjects.forEach(sub => {
            if (sub.score !== null && sub.score !== undefined) {
              totalScore += parseFloat(sub.score);
              marksCount++;
            }
          });

          const percentage = classTotalPossible > 0 ? (totalScore / classTotalPossible) * 100 : 0;

          return {
            ...student,
            totalScore,
            possibleTotalMarks: classTotalPossible,
            percentage,
            subjectsCount: subjects.length,
            marksCount,
            key: student.student_id
          };
        });

        setReportData({
          ...data,
          processedStudents
        });

        // Initial filter
        filterStudents(processedStudents, minPercentage, maxPercentage);
      } catch (error) {
        message.error('Failed to fetch report');
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchReport();
  }, [selectedClass, selectedExam]);

  useEffect(() => {
    if (reportData && reportData.processedStudents) {
      filterStudents(reportData.processedStudents, minPercentage, maxPercentage);
    }
  }, [minPercentage, maxPercentage, reportData]);

  const filterStudents = (students, min, max) => {
    const effectiveMin = (min === null || min === undefined) ? 0 : min;
    const effectiveMax = (max === null || max === undefined) ? 100 : max;
    const filtered = students.filter(s => s.percentage >= effectiveMin && s.percentage <= effectiveMax);
    setFilteredStudents(filtered);

    if (filtered.length > 0) {
      const avgPercentage = (filtered.reduce((sum, s) => sum + s.percentage, 0) / filtered.length).toFixed(2);
      setStats({ average: avgPercentage, total: filtered.length, count: filtered.length });
    } else {
      setStats({ average: 0, total: 0, count: 0 });
    }
  };

  const columns = [
    {
      title: 'Roll No',
      dataIndex: 'roll_no',
      key: 'roll_no',
      width: 80,
    },
    {
      title: 'Admission No',
      dataIndex: 'admission_no',
      key: 'admission_no',
      width: 120,
    },
    {
      title: 'Student Name',
      dataIndex: 'student_name',
      key: 'student_name',
    },
    {
      title: 'Total Score',
      dataIndex: 'totalScore',
      key: 'totalScore',
      render: (score) => score.toFixed(2),
      width: 120,
    },
    {
      title: 'Max Marks',
      dataIndex: 'possibleTotalMarks',
      key: 'possibleTotalMarks',
      width: 120,
    },
    {
      title: 'Subjects',
      key: 'subjects',
      render: (_, record) => `${record.marksCount}/${record.subjectsCount}`,
      width: 100,
    },
    {
      title: 'Percentage',
      key: 'percentage',
      render: (_, record) => {
        const percent = record.percentage.toFixed(2);
        return <Tag color={percent >= 75 ? 'success' : percent >= 50 ? 'orange' : 'red'}>{percent}%</Tag>;
      },
      width: 100,
      sorter: (a, b) => a.percentage - b.percentage,
    },
  ];

  const handleExport = () => {
    if (filteredStudents.length === 0) {
      message.warning('No data to export');
      return;
    }

    const classData = classes.find(c => c.id === selectedClass);
    const examData = exams.find(e => e.id === selectedExam);

    const csv = [
      [`Marks Report (Percentage Range) - ${classData?.name || 'Class'}`],
      [`Exam: ${examData?.name || 'Exam'}`],
      [`Percentage Range: ${minPercentage}% to ${maxPercentage}%`],
      [''],
      ['Roll No', 'Admission No', 'Student Name', 'Total Score', 'Max Marks', 'Percentage'],
      ...filteredStudents.map(s => {
        return [s.roll_no || '-', s.admission_no, s.student_name, s.totalScore.toFixed(2), s.possibleTotalMarks, s.percentage.toFixed(2)];
      })
    ]
      .map(row => row.join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `marks_range_${classData?.name}_${examData?.name}.csv`;
    a.click();
  };

  const handleDownloadPDF = () => {
    if (filteredStudents.length === 0) {
      message.warning('No data to download');
      return;
    }

    const classData = classes.find(c => c.id === selectedClass);
    const examData = exams.find(e => e.id === selectedExam);

    try {
      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 15;
      let yPos = margin;

      // 1. Header
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('KALLADI ABDU HAJI HIGHER SECONDARY SCHOOL', pageWidth / 2, yPos, { align: 'center' });
      yPos += 8;

      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text('Marks Percentage Range Report', pageWidth / 2, yPos, { align: 'center' });
      yPos += 5;

      doc.setFontSize(9);
      doc.setTextColor(150);
      doc.text(`Report Generated: ${dayjs().format('DD-MM-YYYY')}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 12;
      doc.setTextColor(0);

      // 2. Info
      doc.setFontSize(10);
      doc.setFont(undefined, 'bold');
      doc.text(`Class: ${classData?.name || 'N/A'}`, margin, yPos);
      yPos += 5;
      doc.text(`Exam: ${examData?.name || 'N/A'}`, margin, yPos);
      yPos += 5;
      doc.text(`Percentage Range: ${minPercentage}% - ${maxPercentage}%`, margin, yPos);
      yPos += 10;

      // 3. Table
      const head = [['Roll No', 'Adm No', 'Student Name', 'Total Score', 'Max Marks', 'Percentage']];
      const body = filteredStudents.map(s => [
        s.roll_no || '-',
        s.admission_no,
        s.student_name,
        s.totalScore.toFixed(2),
        s.possibleTotalMarks,
        `${s.percentage.toFixed(2)}%`
      ]);

      autoTable(doc, {
        head: head,
        body: body,
        startY: yPos,
        theme: 'grid',
        headStyles: { fillColor: '#333', textColor: '#fff', fontStyle: 'bold', halign: 'center' },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { halign: 'center' },
          1: { halign: 'center' },
          3: { halign: 'right' },
          4: { halign: 'right' },
          5: { halign: 'center' }
        }
      });

      const fileName = `marks_range_${classData?.name}_${examData?.name}.pdf`;
      doc.save(fileName);
      message.success('PDF report downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      message.error('Failed to generate PDF report.');
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <div>
          <Title level={2}>📊 Marks Percentage Range Report</Title>
          <Text type="secondary">
            View student performance across all subjects within a specific percentage range
          </Text>
        </div>

        <Card>
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={6}>
              <label>Select Class *</label>
              <Select
                placeholder="Choose a class"
                value={selectedClass}
                onChange={setSelectedClass}
                loading={loading}
                options={classes.map(c => ({
                  label: c.name,
                  value: c.id,
                }))}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <label>Select Exam *</label>
              <Select
                placeholder="Choose an exam"
                value={selectedExam}
                onChange={setSelectedExam}
                loading={loading}
                options={exams.map(e => ({
                  label: e.name,
                  value: e.id,
                }))}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <label>Min Percentage</label>
              <InputNumber
                min={0}
                max={100}
                value={minPercentage}
                onChange={(val) => setMinPercentage(val)}
                style={{ width: '100%' }}
              />
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <label>Max Percentage</label>
              <InputNumber
                min={0}
                max={100}
                value={maxPercentage}
                onChange={(val) => setMaxPercentage(val)}
                style={{ width: '100%' }}
              />
            </Col>
          </Row>
        </Card>

        {filteredStudents.length > 0 && (
          <>
            <Row gutter={16}>
              <Col xs={12} sm={8}>
                <Statistic title="Average Percentage (Filtered)" value={stats.average} suffix="%" />
              </Col>
              <Col xs={12} sm={8}>
                <Statistic title="Students in Range" value={stats.count} />
              </Col>
            </Row>
            <Divider />
          </>
        )}

        {selectedClass && selectedExam ? (
          <Card
            title={`Total Marks for ${exams.find(e => e.id === selectedExam)?.name} (${minPercentage}% - ${maxPercentage}%)`}
            extra={
              <Space>
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  onClick={handleExport}
                  disabled={filteredStudents.length === 0}
                >
                  Export CSV
                </Button>
                <Button
                  icon={<DownloadOutlined />}
                  onClick={handleDownloadPDF}
                  disabled={filteredStudents.length === 0}
                >
                  Download PDF
                </Button>
              </Space>
            }
          >
            {loading ? (
              <Spin />
            ) : filteredStudents.length > 0 ? (
              <Table
                columns={columns}
                dataSource={filteredStudents}
                pagination={{
                  pageSize: pageSize,
                  showSizeChanger: true,
                  pageSizeOptions: [10, 20, 50, 100],
                  onShowSizeChange: (current, size) => setPageSize(size),
                  onChange: (page, size) => setPageSize(size)
                }}
                scroll={{ x: 900 }}
              />
            ) : (
              <Empty description="No students found in the selected percentage range" />
            )}
          </Card>
        ) : (
          <Empty description="Please select class and exam to view marks" />
        )}
      </Space>
    </div>
  );
};

export default MarkRangeReportPage;
